import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';

// ===============================
// TIPOS CORREGIDOS PARA EL BACKEND REAL
// ===============================

// Para respuestas de CREATE/UPDATE
export interface CreateUpdateResponse<T> {
  success: boolean;
  message: string;
  data: T;
  is_create: boolean;
}

// Para respuestas de DELETE
export interface DeleteResponse {
  success: boolean;
  message: string;
  deleted_id: number;
}

// Para respuestas paginadas - ESTRUCTURA REAL DEL BACKEND
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

// ===============================
// CONFIGURACIÓN BASE
// ===============================

const baseConfig: AxiosRequestConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || '/mava/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
};

// ===============================
// INSTANCIA DE AXIOS
// ===============================

export const apiClient: AxiosInstance = axios.create(baseConfig);

// ===============================
// GESTIÓN DE ESTADO DE CONEXIÓN
// ===============================

let isServerUnavailable = false;
let lastServerCheckTime = 0;
const SERVER_CHECK_COOLDOWN = 5000; // 5 segundos entre verificaciones

/**
 * Verifica si el servidor está disponible
 */
const checkServerAvailability = async (): Promise<boolean> => {
  const now = Date.now();
  
  // Evitar verificaciones muy frecuentes
  if (now - lastServerCheckTime < SERVER_CHECK_COOLDOWN) {
    return !isServerUnavailable;
  }
  
  lastServerCheckTime = now;
  
  try {
    // Health endpoint correcto - usar ruta relativa para que pase por proxy
    const healthUrl = '/health';
    
    await axios.get(healthUrl, { timeout: 5000 });
    
    if (isServerUnavailable) {
      isServerUnavailable = false;
      toast.success('Conexión restablecida');
    }
    
    return true;
  } catch (error) {
    if (!isServerUnavailable) {
      isServerUnavailable = true;
      toast.error('Servidor no disponible. Verifica tu conexión.', {
        duration: 5000,
        id: 'server-unavailable' // Evitar múltiples toasts
      });
    }
    
    return false;
  }
};

// ===============================
// INTERCEPTORES
// ===============================

// Interceptor para requests - agregar token JWT y empresa ID
apiClient.interceptors.request.use(
  async (config) => {
    // Si ya estamos manejando expiración de sesión, no hacer chequeos adicionales
    if (isHandlingSessionExpiry && !config.url?.includes('/auth/')) {
      throw new Error('SESSION_EXPIRED_HANDLED');
    }
    
    const token = localStorage.getItem('mava_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 🏢 MULTITENANT: Agregar header X-Empresa-ID si existe empresa activa
    try {
      const empresaTemporalId = sessionStorage.getItem('mava_empresa_temporal_id');
      if (empresaTemporalId && config.headers) {
        config.headers['X-Empresa-ID'] = empresaTemporalId;
      } else {
        const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
        if (empresaActivaStr && config.headers) {
          const empresaActiva = JSON.parse(empresaActivaStr);
          if (empresaActiva?.empresa?.id) {
            config.headers['X-Empresa-ID'] = empresaActiva.empresa.id.toString();
          }
        }
      }
    } catch (error) {
      // Solo en desarrollo
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error al obtener empresa para header:', error);
      }
    }
    
    return config;
  },
  (error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// Variable para rastrear si ya se está manejando una expiración de sesión
let isHandlingSessionExpiry = false;

// Interceptor para responses - manejo de errores globales mejorado
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Marcar servidor como disponible si la respuesta es exitosa
    if (isServerUnavailable) {
      isServerUnavailable = false;
      toast.dismiss('server-unavailable');
      toast.success('Conexión restablecida');
    }
    
    // Log para debugging (solo en desarrollo)
    // if (import.meta.env.VITE_DEBUG === 'true') {
    //   console.log('✅ API Response:', {
    //     status: response.status,
    //     url: response.config.url,
    //     data: response.data,
    //   });
    // }
    
    return response;
  },
  async (error) => {
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    const isHealthEndpoint = error.config?.url?.includes('/health');
    
    // Manejo específico de errores
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          if (!isAuthEndpoint) {
            // Prevenir múltiples ejecuciones simultáneas
            if (!isHandlingSessionExpiry) {
              isHandlingSessionExpiry = true;
              // Importar dinámicamente para evitar dependencias circulares
              import('./authServiceUAU').then(({ AuthServiceUAU }) => {
                AuthServiceUAU.handleSessionExpired();
                // Reset después de un tiempo para permitir nuevas sesiones
                setTimeout(() => {
                  isHandlingSessionExpiry = false;
                }, 2000);
              }).catch(error => {
                console.error('❌ Error importando AuthServiceUAU:', error);
                // Fallback manual
                localStorage.clear();
                window.location.href = '/login?expired=true';
              });
            }
            
            // Retornar error especial para que no se propague a la UI
            return Promise.reject(new Error('SESSION_EXPIRED_HANDLED'));
          }
          // Para endpoints de auth, dejar que el error se propague normalmente
          break;
          
        case 403:
          if (!isAuthEndpoint && !isHealthEndpoint) {
            toast.error('No tienes permisos para realizar esta acción.', {
              id: 'permission-denied'
            });
          }
          break;
          
        case 404:
          if (!isAuthEndpoint && !isHealthEndpoint) {
            // No mostrar toast para 404, solo logear
            console.warn('⚠️ Recurso no encontrado:', error.config?.url);
          }
          break;
          
        case 400:
          if (!isAuthEndpoint && !isHealthEndpoint) {
            console.warn('⚠️ Error 400:', data?.detail || data?.message || 'Bad Request');
            // No mostrar toast automático para 400, dejarlo al componente
          }
          break;
          
        case 422:
          if (!isAuthEndpoint && !isHealthEndpoint) {
            // Errores de validación - solo logear, el componente debe manejarlos
            console.warn('⚠️ Error de validación:', data);
          }
          break;
          
        case 500:
        case 502:
        case 503:
        case 504:
          if (!isAuthEndpoint && !isHealthEndpoint) {
            toast.error('Error del servidor. Por favor, intenta más tarde.', {
              id: 'server-error'
            });
          }
          break;
          
        default:
          if (!isAuthEndpoint && !isHealthEndpoint) {
            console.warn('⚠️ Error inesperado:', status, data?.message || 'Error desconocido');
          }
      }
    } else if (error.request) {
      // Error de red - PERO NO si ya manejamos un 401
      if (error.message === 'SERVIDOR_NO_DISPONIBLE') {
        // Ya manejado en el interceptor de request
        return Promise.reject(error);
      }
      
      // Solo tratar como error de red si NO es un problema de autenticación
      if (!isAuthEndpoint && !isHealthEndpoint && !isHandlingSessionExpiry) {
        console.warn('⚠️ Network Error detectado:', error.message);
        
        // Verificar conectividad
        if (!navigator.onLine) {
          toast.error('Sin conexión a internet', {
            id: 'no-internet',
            duration: 5000
          });
        } else {
          // Marcar servidor como no disponible y verificar
          isServerUnavailable = true;
          await checkServerAvailability();
        }
      }
    } else {
      // Error de configuración - solo si no estamos manejando expiración
      if (!isAuthEndpoint && !isHealthEndpoint && !isHandlingSessionExpiry) {
        console.error('⚠️ Config Error:', error.message);
      }
    }
    
    return Promise.reject(error);
  }
);

// ===============================
// CLIENTE API MEJORADO
// ===============================

export class ApiClient {
  /**
   * GET request - Retorna directamente los datos sin envolver
   * Con manejo mejorado de errores
   */
  static async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error: any) {
      // Re-lanzar errores manejados por interceptores sin log adicional
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      // No hacer log de errores de autenticación ni si ya estamos manejando expiración
      if (!url.includes('/auth/') && !isHandlingSessionExpiry) {
        console.error(`❌ GET ${url} failed:`, error.response?.data || error.message);
      }
      throw error;
    }
  }

  /**
   * POST request con manejo mejorado de errores
   */
  static async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<CreateUpdateResponse<T>> {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      // No hacer log de errores de autenticación ni si ya estamos manejando expiración
      if (!url.includes('/auth/') && !isHandlingSessionExpiry) {
        console.error(`❌ POST ${url} failed:`, error.response?.data || error.message);
      }
      throw error;
    }
  }

  /**
   * PUT request con manejo mejorado de errores
   */
  static async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<CreateUpdateResponse<T>> {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      console.error(`❌ PUT ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * DELETE request con manejo mejorado de errores
   */
  static async delete(url: string, config?: AxiosRequestConfig): Promise<DeleteResponse> {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      console.error(`❌ DELETE ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * PATCH request con manejo mejorado de errores
   */
  static async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<CreateUpdateResponse<T>> {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      console.error(`❌ PATCH ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Upload de archivos con manejo mejorado de errores
   */
  static async upload<T>(url: string, file: File, additionalData?: any): Promise<T> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (additionalData) {
        Object.keys(additionalData).forEach(key => {
          formData.append(key, additionalData[key]);
        });
      }

      const response = await apiClient.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      console.error(`❌ UPLOAD ${url} failed:`, error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Download de archivos con manejo mejorado de errores
   */
  static async download(url: string, filename?: string): Promise<void> {
    try {
      const response = await apiClient.get(url, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error: any) {
      if (error.message === 'SESSION_EXPIRED_HANDLED' || 
          error.message === 'SERVIDOR_NO_DISPONIBLE') {
        throw error;
      }
      
      console.error(`❌ DOWNLOAD ${url} failed:`, error.response?.data || error.message);
      toast.error('Error al descargar el archivo');
      throw error;
    }
  }

  // ===============================
  // MÉTODOS ESPECIALIZADOS PARA ENDPOINTS ESPECÍFICOS
  // ===============================

  /**
   * Para endpoints que retornan arrays directamente (como /lista)
   * GET /maquinas/lista → Maquina[]
   * GET /operadores/lista → Operador[]
   */
  static async getList<T>(url: string, config?: AxiosRequestConfig): Promise<T[]> {
    return this.get<T[]>(url, config);
  }

  /**
   * Para endpoints paginados
   * GET /maquinas → PaginatedResponse<Maquina>
   */
  static async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedResponse<T>> {
    return this.get<PaginatedResponse<T>>(url, config);
  }

  /**
   * Para endpoints que retornan un objeto individual
   * GET /maquinas/1 → Maquina
   */
  static async getOne<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.get<T>(url, config);
  }

  // ===============================
  // UTILIDADES DE CONECTIVIDAD
  // ===============================

  /**
   * Verifica manualmente la conectividad del servidor
   */
  static async checkConnectivity(): Promise<boolean> {
    return checkServerAvailability();
  }

  /**
   * Obtiene el estado actual de conectividad
   */
  static isServerAvailable(): boolean {
    return !isServerUnavailable;
  }

  // ===============================
  // UTILIDADES PARA CONFIGURACIÓN
  // ===============================

  /**
   * Obtiene la URL base del API
   */
  static getBaseURL(): string {
    return baseConfig.baseURL || '/mava/api/v1';
  }

  /**
   * Obtiene el token de autenticación actual
   */
  static getAuthToken(): string | null {
    return localStorage.getItem('mava_token');
  }

  /**
   * Obtiene el ID de la empresa activa
   */
  static getEmpresaId(): string | null {
    try {
      // Primero verificar empresa temporal
      const empresaTemporalId = sessionStorage.getItem('mava_empresa_temporal_id');
      if (empresaTemporalId) {
        return empresaTemporalId;
      }
      
      // Luego empresa activa
      const empresaActivaStr = localStorage.getItem('mava_empresa_activa');
      if (empresaActivaStr) {
        const empresaActiva = JSON.parse(empresaActivaStr);
        return empresaActiva?.empresa?.id?.toString() || null;
      }
      
      return null;
    } catch (error) {
      console.warn('Error al obtener empresa ID:', error);
      return null;
    }
  }
}

// ===============================
// INICIALIZACIÓN DE MONITOREO DE CONECTIVIDAD
// ===============================

// Monitorear cambios en la conectividad del navegador
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    // console.log('🌐 Conexión a internet restablecida');
    checkServerAvailability();
  });

  window.addEventListener('offline', () => {
    // console.log('🌐 Conexión a internet perdida');
    toast.error('Sin conexión a internet', {
      id: 'no-internet',
      duration: 5000
    });
  });
}

export default apiClient;